import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import laboklinTheme, { LABOKLIN_PAGE_BG } from '../theme/laboklinTheme';
import LaboklinChrome from '../components/mylab/LaboklinChrome';
import ReportsListView from '../components/mylab/ReportsListView';
import ReportDetailView from '../components/mylab/ReportDetailView';
import SubmissionFormView from '../components/mylab/SubmissionFormView';
import CopilotPanel from '../components/mylab/CopilotPanel';

/**
 * Laboklin MyLab portal view with the GekkoVet assistant embedded.
 *
 * Not linked from navigation; reached directly at /mylab. It sits inside the
 * normal auth and subscription gates like every other route, and is skinned
 * with the Laboklin theme rather than GekkoVet's.
 */

interface PanelState {
  initialMessage: string;
  labMode: 'pre_order' | 'post_result';
  labReportNo?: string;
}

interface MyLabPageProps {
  signOut: () => void;
}

const TITLES: Array<[RegExp, string]> = [
  [/^\/mylab\/reports\/(.+)$/, 'Report'],
  [/^\/mylab\/order\/new$/, 'Online order'],
  [/^\/mylab\/?$/, 'Reports'],
];

export const MyLabPage: React.FC<MyLabPageProps> = ({ signOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [panel, setPanel] = useState<PanelState | null>(null);

  const title =
    TITLES.find(([pattern]) => pattern.test(location.pathname))?.[1] ?? 'MyLab';

  const openPanel = useCallback(
    (next: PanelState) => {
      setPanel(next);
      // Keeps the panel open across an accidental refresh mid-demo.
      const params = new URLSearchParams(searchParams);
      params.set('copilot', '1');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const closePanel = useCallback(() => {
    setPanel(null);
    const params = new URLSearchParams(searchParams);
    params.delete('copilot');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Ask for an interpretation.
   *
   * Phrased as an explicit request for differential diagnoses, because that is the
   * condition the diagnostic-case tool requires before it will work a patient up:
   * without it the request reads as document review and the answer comes from the
   * model rather than from the knowledge base.
   *
   * The clinical signs are deliberately NOT restated here. The report carries them
   * and get_lab_report returns them, so repeating them in the opening message would
   * only be a second copy to drift from the laboratory's own wording.
   */
  const interpretReport = useCallback(
    (reportNo: string) => {
      openPanel({
        initialMessage:
          `Interpret Laboklin report ${reportNo}. Give me the differential diagnoses for ` +
          'this patient and tell me what to do next.',
        labMode: 'post_result',
        labReportNo: reportNo,
      });
    },
    [openPanel]
  );

  const askAboutReportFromList = useCallback(
    (reportNo: string) => {
      navigate(`/mylab/reports/${reportNo}`);
      interpretReport(reportNo);
    },
    [navigate, interpretReport]
  );

  const askWhichTests = useCallback(
    (summary: string) => openPanel({ initialMessage: summary, labMode: 'pre_order' }),
    [openPanel]
  );

  return (
    <ThemeProvider theme={laboklinTheme}>
      <CssBaseline />
      {/* The app shell paints a photographic background with a transparent body;
          MyLab has to lay its own opaque surface over it or the portal chrome
          reads against the photo. */}
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: LABOKLIN_PAGE_BG,
          position: 'relative',
          zIndex: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <LaboklinChrome title={title} userLabel="Niklas Närhinen (136244)" onSignOut={signOut}>
            <Routes>
              <Route path="/" element={<ReportsListView onAskAboutReport={askAboutReportFromList} />} />
              <Route path="reports/:reportNo" element={<ReportDetailView onInterpret={interpretReport} />} />
              <Route path="order/new" element={<SubmissionFormView onAskWhichTests={askWhichTests} />} />
              <Route path="*" element={<Navigate to="/mylab" replace />} />
            </Routes>
          </LaboklinChrome>
        </Box>

        {panel && (
          <CopilotPanel
            initialMessage={panel.initialMessage}
            labMode={panel.labMode}
            labReportNo={panel.labReportNo}
            onClose={closePanel}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MyLabPage;
